'use client';

import { useState, useRef, useEffect, useCallback } from 'react';
import { useCheckout } from '../checkout-context';
import { formatPrice, calculateTotalPrice } from '@/app/config/plans';
import { createSubscription, validatePromoCode, isPromoCodeValid } from '@/app/utils/checkout-api';
import { isAuthSuccess } from '@/app/utils/auth-api';
import { useTracking } from '@/components/tracking-provider';
import {
  buildProFormViewPaymentParams,
  buildProPaymentCompleteParams,
  extractErrorMessage,
} from '@/app/utils/checkout-tracking';
import CreditCardForm, { type CreditCardFormRef } from '../payment/credit-card-form';
import PromoCodeInput from '../payment/promo-code-input';
import YearlyUpsell from '../payment/yearly-upsell';
import PaymentMethodSelector, { type PaymentMethod } from '../payment/payment-method-selector';
import PayPalButton from '../payment/paypal-button';
import type { PromoCodeValidationResult } from '@/app/utils/checkout-api';
import { AmexIcon, GenericCardIcon, MastercardIcon, VisaIcon } from '../payment/icons';

export default function PaymentStep() {
  const tracker = useTracking();
  const {
    state,
    selectedPlan,
    handleChangePlan,
    handlePaymentSuccess,
    setYears,
    setError,
    setProcessing,
  } = useCheckout();

  const [promoResult, setPromoResult] = useState<PromoCodeValidationResult | null>(null);
  const [promoCode, setPromoCode] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>(null);
  const [canSubmitCard, setCanSubmitCard] = useState(false);
  const [cardType, setCardType] = useState<string | null>(null);
  const cardFormRef = useRef<CreditCardFormRef | null>(null);
  const prevYearsRef = useRef(state.selectedYears);

  // Track payment step view
  useEffect(() => {
    tracker.trackEvent('Checkout Payment Started', {
      tier: state.selectedTier,
      years: state.selectedYears,
    });

    // Fire legacy 'Pro Form View Payment' event for A/B test compatibility
    if (state.selectedTier) {
      const proFormParams = buildProFormViewPaymentParams({
        tier: state.selectedTier,
        years: state.selectedYears,
        userId: state.user?.id ?? null,
        source: state.source,
        isNewUser: state.authMode === 'signup',
      });
      tracker.trackEvent('Pro Form View Payment', proFormParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Re-validate promo code when years change and PromoCodeInput is not visible
  useEffect(() => {
    // Skip if years haven't changed
    if (prevYearsRef.current === state.selectedYears) {
      return;
    }
    prevYearsRef.current = state.selectedYears;

    // Only re-validate if we have a promo code and the PromoCodeInput is hidden
    if (!promoCode || paymentMethod === null) {
      return;
    }

    // Re-validate the promo code with new years value
    const revalidate = async () => {
      const newBasePrice = calculateTotalPrice(state.selectedTier!, state.selectedYears);

      try {
        const result = await validatePromoCode({
          promoCode,
          tier: state.selectedTier!,
          subscriptionLength: state.selectedYears,
          price: newBasePrice,
        });

        if (isPromoCodeValid(result)) {
          setPromoResult(result);
        } else {
          // Validation failed - clear the promo code
          setPromoResult(null);
          setPromoCode(null);
        }
      } catch {
        // Error validating - clear the promo code
        setPromoResult(null);
        setPromoCode(null);
      }
    };

    revalidate();
  }, [state.selectedYears, state.selectedTier, promoCode, paymentMethod]);

  // Handle payment submission with nonce (shared by card and PayPal)
  // Must be defined before early return to satisfy React hooks rules
  const handlePaymentWithNonce = useCallback(
    async (nonce: string, method: 'card' | 'paypal') => {
      if (!state.selectedTier) return;

      setProcessing(true);
      setError(null);

      try {
        // Submit the subscription
        const result = await createSubscription({
          tier: state.selectedTier,
          subscriptionLength: state.selectedYears,
          paymentMethodNonce: nonce,
          promoCode: promoCode || undefined,
        });

        if (isAuthSuccess(result)) {
          // Fire legacy 'Pro Payment Complete' event for A/B test compatibility
          const paymentType = method === 'paypal' ? 'paypal' : cardType;
          const proPaymentParams = buildProPaymentCompleteParams({
            tier: state.selectedTier,
            years: state.selectedYears,
            paymentType,
            promoCode,
            promoDiscount: promoResult?.discount ?? null,
            source: state.source,
          });
          tracker.trackEvent('Pro Payment Complete', proPaymentParams);

          // Track GA4 purchase event
          const basePrice = calculateTotalPrice(state.selectedTier, state.selectedYears);
          const paymentTotal = promoResult ? promoResult.total : basePrice;
          tracker.trackPurchase({
            userId: state.user?.id,
            paymentTotal,
            subscriptionPackage: state.selectedTier,
            subscriptionLength: state.selectedYears,
            promoCode: promoCode ?? undefined,
            promoCodeDiscount: promoResult?.discount,
          });

          handlePaymentSuccess(result);
        } else {
          tracker.trackEvent('Checkout Payment Error', {
            tier: state.selectedTier,
            error: result.error.message,
            type: 'api_error',
            method,
          });
          setError(result.error.message);
        }
      } catch (err) {
        const errorMessage = extractErrorMessage(err);
        tracker.trackEvent('Checkout Payment Error', {
          tier: state.selectedTier,
          error: errorMessage,
          type: 'subscription_error',
          method,
        });
        setError(errorMessage);
      } finally {
        setProcessing(false);
      }
    },
    [
      state.selectedTier,
      state.selectedYears,
      state.source,
      state.user?.id,
      promoCode,
      promoResult,
      cardType,
      handlePaymentSuccess,
      setProcessing,
      setError,
      tracker,
    ],
  );

  // Handle PayPal approval - receives nonce from PayPal button
  const handlePayPalApprove = useCallback(
    async (nonce: string) => {
      await handlePaymentWithNonce(nonce, 'paypal');
    },
    [handlePaymentWithNonce],
  );

  if (!selectedPlan || !state.selectedTier) return null;

  // Calculate prices
  const basePrice = calculateTotalPrice(state.selectedTier, state.selectedYears);
  const finalPrice = promoResult ? promoResult.total : basePrice;
  const hasDiscount = !!(promoResult && promoResult.discount > 0);

  // Handle promo code validation
  const handlePromoValidated = (result: PromoCodeValidationResult | null, code: string | null) => {
    setPromoResult(result);
    setPromoCode(code);
  };

  // Handle payment method selection
  const handlePaymentMethodSelect = (method?: 'card' | 'paypal' | undefined) => {
    if (!method) {
      setError(null);
      setPaymentMethod(null);
      return;
    }
    setPaymentMethod(method);
    tracker.trackEvent('Checkout Payment Method Selected', {
      method,
      tier: state.selectedTier,
    });
  };

  // Handle credit card submission
  const handleSubmit = async () => {
    if (!cardFormRef.current?.canTokenize) {
      setError('Please fill in all payment fields');
      return;
    }

    setProcessing(true);
    setError(null);

    try {
      // Tokenize the card
      const { nonce } = await cardFormRef.current.tokenize();
      await handlePaymentWithNonce(nonce, 'card');
    } catch (err) {
      const errorMessage = extractErrorMessage(err);
      tracker.trackEvent('Checkout Payment Error', {
        tier: state.selectedTier,
        error: errorMessage,
        type: 'tokenization_error',
      });
      setError(errorMessage);
      setProcessing(false);
    }
  };

  return (
    <div>
      <h2 className="text-2xl font-bold mb-6 font-merriweather">Finalize payment</h2>

      {/* Plan summary with year selector for yearly plans */}
      <div className="mb-6">
        {/* Multi-year selector for yearly plans - "change" link lives in its first row */}
        {selectedPlan.supportsMultiYear ? (
          <YearlyUpsell
            plan={selectedPlan}
            selectedYears={state.selectedYears}
            onYearsChange={setYears}
            onChangePlan={handleChangePlan}
            disabled={state.isProcessing}
            variant="addRemove"
          />
        ) : (
          // Non-yearly plans (monthly, lifetime) - simple display
          <>
            <div className="flex justify-between items-center mb-2">
              <span className="text-zinc-800 font-semibold">Your plan</span>
              <button
                onClick={handleChangePlan}
                className="text-sm text-zinc-800 hover:text-zinc-900 cursor-pointer transition-colors underline"
              >
                Change plan
              </button>
            </div>
            <div className="bg-slate-50 rounded-lg p-4">
              <div className="flex justify-between items-center">
                <span className="font-medium">{selectedPlan.displayName}</span>
                <span className="font-medium">{formatPrice(basePrice)}</span>
              </div>
              {selectedPlan.periodLabel && (
                <span className="text-sm text-slate-500">{selectedPlan.periodLabel}</span>
              )}
            </div>
          </>
        )}
      </div>

      {/* Payment Method Selection */}
      {paymentMethod === null && (
        <PaymentMethodSelector
          selected={paymentMethod}
          onSelect={handlePaymentMethodSelect}
          disabled={state.isProcessing}
        />
      )}

      {/* Credit Card Form - only when card selected */}
      {paymentMethod === 'card' && (
        <div className="mb-6 border rounded border-neutral-300">
          <div className="flex flex-row justify-between p-3 border-b border-neutral-300 bg-blue-50">
            <div className="inline-flex flex-row">
              <GenericCardIcon className="w-8 h-6 mr-4" />
              <span className="font-semibold text-sm text-zinc-800">Pay with card</span>
            </div>
            <div className="inline-flex flex-row">
              <VisaIcon className="w-8 h-6 mr-2" />
              <MastercardIcon className="w-8 h-6 mr-2" />
              <AmexIcon className="w-8 h-6" />
            </div>
          </div>
          <div className="p-3">
            <CreditCardForm
              formRef={cardFormRef}
              disabled={state.isProcessing}
              onError={(error) => setError(error)}
              onValidityChange={setCanSubmitCard}
              onCardTypeChange={setCardType}
            />
          </div>
        </div>
      )}

      {/* PayPal Button - only when paypal selected */}
      {paymentMethod === 'paypal' && (
        <PayPalButton
          amount={finalPrice}
          onApprove={handlePayPalApprove}
          onError={(error) => setError(error.message)}
          disabled={state.isProcessing}
        />
      )}

      {/* Promo Code - only on payment method selection screen */}
      {paymentMethod === null && (
        <div className="mb-10">
          <PromoCodeInput
            planTier={state.selectedTier}
            years={state.selectedYears}
            basePrice={basePrice}
            onValidated={handlePromoValidated}
            disabled={state.isProcessing}
            initialCode={promoCode}
            initialResult={promoResult}
          />
        </div>
      )}

      {/* Error Display */}
      {state.error && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <p className="text-red-600 text-sm">{state.error}</p>
        </div>
      )}

      {/* Order Summary - subtotal/discount rows only shown when a promo discount applies */}
      <div className="mb-4 p-3 border border-neutral-300 rounded">
        {hasDiscount && promoResult && (
          <>
            <div className="flex justify-between items-center text-sm">
              <span className="text-slate-600">Subtotal</span>
              <span>{formatPrice(basePrice)}</span>
            </div>
            <div className="flex justify-between items-center text-sm mt-2">
              <span className="text-emerald-600">Discount</span>
              <span className="text-emerald-600">-{formatPrice(promoResult.discount)}</span>
            </div>
          </>
        )}
        <div className={hasDiscount ? 'border-t border-slate-200 mt-3 pt-3' : ''}>
          <div className="flex justify-between items-center font-medium">
            <span>Total</span>
            <span className="text-lg">{formatPrice(finalPrice)}</span>
          </div>
        </div>
      </div>

      {paymentMethod && (
        <div className="mb-6 text-center">
          <button
            onClick={() => handlePaymentMethodSelect()}
            className="text-sm text-zinc-800 hover:text-zinc-900 cursor-pointer underline transition-colors text-center mx-auto"
          >
            Choose another way to pay
          </button>
        </div>
      )}

      {/* Submit Button - hidden for PayPal (has its own button) */}
      {paymentMethod !== 'paypal' && (
        <div className={paymentMethod ? '' : 'pt-2'}>
          <button
            onClick={handleSubmit}
            disabled={
              state.isProcessing || !paymentMethod || (paymentMethod === 'card' && !canSubmitCard)
            }
            className="btn w-full bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {state.isProcessing ? (
              <span className="flex items-center justify-center gap-2">
                <svg className="animate-spin w-5 h-5" fill="none" viewBox="0 0 24 24">
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  />
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  />
                </svg>
                Processing...
              </span>
            ) : (
              'Confirm and pay'
            )}
          </button>
        </div>
      )}

      {/* Security note */}
      {/* <p className="text-xs text-slate-500 text-center mt-4">
        Your payment is secured by Braintree. We never store your card details.
      </p> */}
    </div>
  );
}
